/**
 * tdesign v1.0.3
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _toConsumableArray } from '../_chunks/dep-fd3498b6.js';
import { _ as _defineProperty } from '../_chunks/dep-46de7b2e.js';
import { _ as _slicedToArray } from '../_chunks/dep-e43cf21c.js';
import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { m as merge_1 } from '../_chunks/dep-d71ee5f3.js';
import { g as get_1 } from '../_chunks/dep-337b4140.js';
import { useFormContext, FormListContext } from './FormContext.js';
import { HOOK_MARK } from './hooks/useForm.js';
import { calcFieldValue } from './utils/index.js';
import '../_common/js/log/index.js';
import log from '../_common/js/log/log.js';
import '../_chunks/dep-2acfafca.js';
import '../_chunks/dep-c3f1141e.js';
import '../_chunks/dep-4b8f240e.js';
import '../_chunks/dep-9acbc163.js';
import '../_chunks/dep-7b52f080.js';
import '../_chunks/dep-f10515e8.js';
import '../_chunks/dep-4941ebe7.js';
import '../_chunks/dep-e9b62d1f.js';
import '../_chunks/dep-88cd50bf.js';
import '../_chunks/dep-dcb5aa2a.js';
import '../_chunks/dep-ee4bf8c2.js';
import '../_chunks/dep-934eda0c.js';
import '../_chunks/dep-7dce2c26.js';
import '../_chunks/dep-12510d64.js';
import '../_chunks/dep-6b568d83.js';
import '../_chunks/dep-304e657b.js';
import '../_chunks/dep-77e34309.js';
import '../_chunks/dep-6506fbc2.js';
import '../_chunks/dep-bb5d7b1e.js';
import '../_chunks/dep-e285f2f8.js';
import '../_chunks/dep-f4a320cd.js';
import '../_chunks/dep-ec70b95c.js';
import '../_chunks/dep-ca64038e.js';
import '../_chunks/dep-c1f93350.js';
import '../_chunks/dep-33ffecad.js';
import '../_chunks/dep-e51b1270.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var key = 0;
var FormList = function FormList(props) {
  var _useFormContext = useFormContext(),
    formMapRef = _useFormContext.formMapRef,
    form = _useFormContext.form,
    onFormItemValueChange = _useFormContext.onFormItemValueChange;
  var name = props.name,
    _props$initialData = props.initialData,
    initialData = _props$initialData === void 0 ? [] : _props$initialData,
    rules = props.rules,
    children = props.children;
  var _useState = useState(initialData),
    _useState2 = _slicedToArray(_useState, 2),
    formListValue = _useState2[0],
    setFormListValue = _useState2[1];
  var _useState3 = useState(initialData.map(function (data, index) {
      return _objectSpread({
        key: key += 1,
        name: index,
        isListField: true
      }, data);
    })),
    _useState4 = _slicedToArray(_useState3, 2),
    fields = _useState4[0],
    setFields = _useState4[1];
  var formListMapRef = useRef( /* @__PURE__ */new Map());
  var formListRef = useRef();
  var fieldsTaskQueueRef = useRef([]);
  var snakeName = [].concat(name).filter(function (item) {
    return item !== void 0;
  }).join("_");
  var operation = {
    add: function add(defaultValue, insertIndex) {
      var cloneFields = _toConsumableArray(fields);
      var index = insertIndex !== null && insertIndex !== void 0 ? insertIndex : cloneFields.length;
      cloneFields.splice(index, 0, {
        key: key += 1,
        name: index,
        isListField: true
      });
      cloneFields.forEach(function (field, index2) {
        return Object.assign(field, {
          name: index2
        });
      });
      setFields(cloneFields);
      var nextFormListValue = _toConsumableArray(formListValue);
      if (typeof defaultValue !== "undefined") {
        nextFormListValue[index] = defaultValue;
        setFormListValue(nextFormListValue);
      }
      var fieldValue = calcFieldValue(name, nextFormListValue);
      Promise.resolve().then(function () {
        onFormItemValueChange === null || onFormItemValueChange === void 0 ? void 0 : onFormItemValueChange(_objectSpread({}, fieldValue));
      });
    },
    remove: function remove(index) {
      var nextFields = fields.filter(function (item) {
        if (Array.isArray(index)) return !index.includes(item.name);
        return item.name !== index;
      }).map(function (field, i) {
        return _objectSpread(_objectSpread({}, field), {}, {
          name: i
        });
      });
      setFields(nextFields);
      var nextFormListValue = formListValue.filter(function (_, idx) {
        return idx !== index;
      });
      setFormListValue(nextFormListValue);
      var fieldValue = calcFieldValue(name, nextFormListValue);
      Promise.resolve().then(function () {
        onFormItemValueChange === null || onFormItemValueChange === void 0 ? void 0 : onFormItemValueChange(_objectSpread({}, fieldValue));
      });
    },
    move: function move(from, to) {
      var cloneFields = _toConsumableArray(fields);
      var fromItem = _objectSpread({}, cloneFields[from]);
      var toItem = _objectSpread({}, cloneFields[to]);
      cloneFields[to] = fromItem;
      cloneFields[from] = toItem;
      setFields(cloneFields);
    }
  };
  function setListFields(fieldData, callback, originData) {
    setFields(fieldData.map(function (_, index) {
      return {
        key: key += 1,
        name: index,
        isListField: true
      };
    }));
    fieldsTaskQueueRef.current.push({
      callback: callback,
      fieldData: fieldData,
      originData: originData
    });
  }
  useEffect(function () {
    if (!name || !formMapRef) return;
    formMapRef.current.set(name, formListRef);
    return function () {
      formMapRef.current["delete"](name);
    };
  }, [name]);
  useEffect(function () {
    _toConsumableArray(formListMapRef.current.values()).forEach(function (formItemRef) {
      if (!formItemRef.current) return;
      var _formItemRef$current = formItemRef.current,
        name2 = _formItemRef$current.name,
        isUpdated = _formItemRef$current.isUpdated;
      if (isUpdated) return;
      var data = get_1(formListValue, name2);
      formItemRef.current.setField({
        value: data,
        status: "not"
      });
    });
  }, [formListValue]);
  useEffect(function () {
    var _form$getInternalHook, _form$getInternalHook2, _form$getInternalHook3;
    form === null || form === void 0 ? void 0 : (_form$getInternalHook = form.getInternalHooks) === null || _form$getInternalHook === void 0 ? void 0 : (_form$getInternalHook2 = _form$getInternalHook.call(form, HOOK_MARK)) === null || _form$getInternalHook2 === void 0 ? void 0 : (_form$getInternalHook3 = _form$getInternalHook2.notifyWatch) === null || _form$getInternalHook3 === void 0 ? void 0 : _form$getInternalHook3.call(_form$getInternalHook2, name);
    Promise.resolve().then(function () {
      if (!fieldsTaskQueueRef.current.length) return;
      var currentQueue = fieldsTaskQueueRef.current[0];
      var fieldData = currentQueue.fieldData,
        callback = currentQueue.callback,
        originData = currentQueue.originData;
      _toConsumableArray(formListMapRef.current.values()).forEach(function (formItemRef) {
        if (!formItemRef.current) return;
        var itemName = formItemRef.current.name;
        var data = get_1(fieldData, itemName);
        callback(formItemRef, data);
        fieldsTaskQueueRef.current.pop();
      });
      if (!formMapRef || !formMapRef.current) return;
      _toConsumableArray(formMapRef.current.values()).forEach(function (formItemRef) {
        if (!formItemRef.current) return;
        var _formItemRef$current2 = formItemRef.current,
          itemName = _formItemRef$current2.name,
          isFormList = _formItemRef$current2.isFormList;
        if (String(itemName) === String(name) || !isFormList) return;
        var data = get_1(originData, itemName);
        if (data) callback(formItemRef, data);
      });
    });
  }, [form, name, fields, formMapRef]);
  useImperativeHandle(formListRef, function () {
    return {
      name: name,
      isFormList: true,
      getValue: function getValue() {
        var formListValue2 = [];
        _toConsumableArray(formListMapRef.current.values()).forEach(function (formItemRef) {
          if (!formItemRef.current) return;
          var _formItemRef$current3 = formItemRef.current,
            name2 = _formItemRef$current3.name,
            getValue = _formItemRef$current3.getValue;
          var fieldValue = calcFieldValue(name2, getValue());
          merge_1(formListValue2, fieldValue);
        });
        return formListValue2;
      },
      validate: function validate() {
        var trigger = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "all";
        var resultList = [];
        var validates = _toConsumableArray(formListMapRef.current.values()).map(function (formItemRef) {
          var _formItemRef$current4, _formItemRef$current5;
          return formItemRef === null || formItemRef === void 0 ? void 0 : (_formItemRef$current4 = formItemRef.current) === null || _formItemRef$current4 === void 0 ? void 0 : (_formItemRef$current5 = _formItemRef$current4.validate) === null || _formItemRef$current5 === void 0 ? void 0 : _formItemRef$current5.call(_formItemRef$current4, trigger);
        });
        return new Promise(function (resolve) {
          Promise.all(validates).then(function (validateResult) {
            validateResult.forEach(function (result) {
              var errorValue = Object.values(result)[0];
              merge_1(resultList, errorValue);
            });
            var errorItems = validateResult.filter(function (item) {
              return Object.values(item)[0] !== true;
            });
            if (errorItems.length) {
              resolve(_defineProperty({}, snakeName, resultList));
            } else {
              resolve(_defineProperty({}, snakeName, true));
            }
          });
        });
      },
      setValue: function setValue(fieldData, originData) {
        setListFields(fieldData, function (formItemRef, data) {
          var _formItemRef$current6, _formItemRef$current7;
          formItemRef === null || formItemRef === void 0 ? void 0 : (_formItemRef$current6 = formItemRef.current) === null || _formItemRef$current6 === void 0 ? void 0 : (_formItemRef$current7 = _formItemRef$current6.setValue) === null || _formItemRef$current7 === void 0 ? void 0 : _formItemRef$current7.call(_formItemRef$current6, data);
        }, originData);
      },
      setField: function setField(fieldData, originData) {
        var value = fieldData.value,
          status = fieldData.status;
        setListFields(value, function (formItemRef, data) {
          var _formItemRef$current8, _formItemRef$current9;
          formItemRef === null || formItemRef === void 0 ? void 0 : (_formItemRef$current8 = formItemRef.current) === null || _formItemRef$current8 === void 0 ? void 0 : (_formItemRef$current9 = _formItemRef$current8.setField) === null || _formItemRef$current9 === void 0 ? void 0 : _formItemRef$current9.call(_formItemRef$current8, {
            value: data,
            status: status
          });
        }, originData);
      },
      resetField: function resetField() {
        _toConsumableArray(formListMapRef.current.values()).forEach(function (formItemRef) {
          var _formItemRef$current10, _formItemRef$current11;
          formItemRef === null || formItemRef === void 0 ? void 0 : (_formItemRef$current10 = formItemRef.current) === null || _formItemRef$current10 === void 0 ? void 0 : (_formItemRef$current11 = _formItemRef$current10.resetField) === null || _formItemRef$current11 === void 0 ? void 0 : _formItemRef$current11.call(_formItemRef$current10);
        });
        setFormListValue([]);
      },
      setValidateMessage: function setValidateMessage(fieldData) {
        _toConsumableArray(formListMapRef.current.values()).forEach(function (formItemRef) {
          var _formItemRef$current12, _formItemRef$current13;
          if (!formItemRef.current) return;
          var name2 = formItemRef.current.name;
          var data = get_1(fieldData, name2);
          formItemRef === null || formItemRef === void 0 ? void 0 : (_formItemRef$current12 = formItemRef.current) === null || _formItemRef$current12 === void 0 ? void 0 : (_formItemRef$current13 = _formItemRef$current12.setValidateMessage) === null || _formItemRef$current13 === void 0 ? void 0 : _formItemRef$current13.call(_formItemRef$current12, data);
        });
      },
      resetValidate: function resetValidate() {
        _toConsumableArray(formListMapRef.current.values()).forEach(function (formItemRef) {
          var _formItemRef$current14, _formItemRef$current15;
          formItemRef === null || formItemRef === void 0 ? void 0 : (_formItemRef$current14 = formItemRef.current) === null || _formItemRef$current14 === void 0 ? void 0 : (_formItemRef$current15 = _formItemRef$current14.resetValidate) === null || _formItemRef$current15 === void 0 ? void 0 : _formItemRef$current15.call(_formItemRef$current14);
        });
      }
    };
  });
  if (typeof children !== "function") {
    log.error("Form", "FormList's children must be a function!");
    return null;
  }
  return /* @__PURE__ */React.createElement(FormListContext.Provider, {
    value: {
      name: name,
      rules: rules,
      formListMapRef: formListMapRef
    }
  }, children(fields, operation));
};
FormList.displayName = "FormList";

export { FormList as default };
//# sourceMappingURL=FormList.js.map
